import FontService from "@/services/font-service";
import store from "@/store/index";
import Utils from "@/utils";
import _ from "lodash";
import moment from "moment";
// Add import for font constants
import * as Fonts from "@/constants/calendar-fonts";

let CalendarUtils = {
    descriptionLinesCache: {},

    /**
     * Get dates within month.
     * @param {Number} year
     * @param {Number} month
     * @returns {moment.Moment[]}
     */
    getMonthDates(year, month) {
        let start_date = moment([year, month - 1]).startOf("month");
        let end_date = start_date.clone().endOf("month");
        start_date = start_date.subtract(start_date.day(), "day");
        let running_date = start_date.clone();
        let dates = [];
        while (running_date.isSameOrBefore(end_date)) {
            dates.push(running_date.clone());
            running_date.add(1, "day");
        }
        return dates;
    },
    /**
     * Get the count of weeks in a month.
     * @param year
     * @param month
     * @returns {number}
     */
    getWeekCount(year, month) {
        let start_date = moment([year, month - 1]).startOf("month");
        let end_date = start_date.clone().endOf("month").startOf("day");
        if (end_date.month() === 11) {
            // december
            if (end_date.day() !== 6) {
                return end_date.weeksInYear() + 2 - start_date.week();
            }
        }
        return end_date.week() + 1 - start_date.week();
    },
    getValidFontName(font, is_bold) {
        if (is_bold) {
            return store.state.config.settings.font.bold_map[font];
        }
        return font;
    },
    /**
     * Check if font is a bold font.
     * @param {String} font
     * @returns {Boolean}
     */
    isBoldFont(font) {
        //get values from the key-value array store.state.config.settings.font.bold_map
        let boldMap = store.state.config.settings.font.bold_map || {};
        let boldFontValues = Object.values(boldMap);
        if (boldFontValues.includes(font)) {
            return true;
        }

        //return font.toLowerCase().includes("bold");
    },

    getFontWeight(font) {
        return FontService.getFontWeightFromCSS(font);
    },

    /**
     * Get the maximum lines for each date.
     * @param {Number} year
     * @param {Number} month
     * @param {String} font_size
     * @returns {null|Number}
     */
    getMaxLines(year, month, font_size) {
        // Get the week count for this month
        const weekCount = this.getWeekCount(year, month);

        // Get max lines from config
        const maxLinesConfig =
            store.state.config.settings.calendar.event_max_lines;

        // If we have config values for this week count and font size, use them
        if (
            maxLinesConfig &&
            maxLinesConfig[weekCount] &&
            maxLinesConfig[weekCount][font_size]
        ) {
            return maxLinesConfig[weekCount][font_size];
        }

        return null;
    },

    /**
     * Get the max width of event description, without the time width.
     */
    getEventDescriptionMaxWidth(
        event,
        font,
        font_size = 10,
        first_event = false
    ) {
        let max = 0;
        // Base widths by font size
        switch (font_size) {
            case 7:
                max = 174;
                break;
            case 8:
                max = 174;
                break;
            case 9:
                max = 165;
                break;
            case 10:
                max = 165;
                break;
            case 11:
                max = 158;
                break;
            case 12:
                max = 158;
                break;
            default:
                throw new Error("Invalid font size: " + font_size);
        }

        let fontAdjustments = this.getFontAdjustments(font_size);

        // Get the actual font being used
        const fontName = this.getValidFontName(font, event.is_bold);

        // Apply adjustment if one exists for this font
        if (fontAdjustments[fontName]) {
            max -= fontAdjustments[fontName];
        }

        // First event adjustment
        if (first_event && !event.is_all_day) {
            max -= 16;
        }

        return max;
    },

    /**
     * Get font adjustments based on browser type and font size
     * @param {Number} font_size - Font size in pt
     * @returns {Object} - Font adjustment values
     */
    getFontAdjustments(font_size) {
        const isFirefox = navigator.userAgent.indexOf("Firefox") > -1;
        const isEdge = navigator.userAgent.indexOf("Edg") > -1;
        const isSafari =
            navigator.userAgent.indexOf("Safari") > -1 &&
            navigator.userAgent.indexOf("Chrome") === -1;
        const isChrome = navigator.userAgent.indexOf("Chrome") > -1 && !isEdge;

        // Try to get adjustments from store first
        const storeAdjustments = store.state.fontAdjustments.adjustments;

        if (storeAdjustments) {
            const browserType = isFirefox
                ? "firefox"
                : isEdge
                ? "edge"
                : isSafari
                ? "safari"
                : "chrome";
            if (
                storeAdjustments[browserType] &&
                storeAdjustments[browserType][font_size]
            ) {
                return storeAdjustments[browserType][font_size];
            }
        }
        console.warn(
            "no font adjustments found in store, using default adjustments"
        );
        // If no adjustments found in store, use default adjustments
        // Default adjustments (Chrome and others)
        let _font_size_adjustments = {
            7: {
                [Fonts.AVENIR_NEXT_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                [Fonts.AVENIR_NEXT_LIGHT]: 2,
                [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                [Fonts.CRIMSONPRO_BOLD]: 2,
                [Fonts.CRIMSONPRO_BOLDER]: 2,
                [Fonts.CRIMSONPRO_MEDIUM]: 2,
                [Fonts.HELVETICA]: 3,
                [Fonts.HELVETICA_BOLD]: 2,
                [Fonts.HELVETICA_NARROW]: 2,
                [Fonts.HELVETICA_NARROW_BOLD]: 2
            },
            8: {
                [Fonts.AVENIR_NEXT_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                [Fonts.AVENIR_NEXT_LIGHT]: 2,
                [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                [Fonts.CRIMSONPRO_BOLD]: 2,
                [Fonts.CRIMSONPRO_BOLDER]: 2,
                [Fonts.CRIMSONPRO_MEDIUM]: 2,
                [Fonts.HELVETICA]: 3,
                [Fonts.HELVETICA_BOLD]: 2,
                [Fonts.HELVETICA_NARROW]: 2,
                [Fonts.HELVETICA_NARROW_BOLD]: 2
            },
            9: {
                [Fonts.AVENIR_NEXT_BOLD]: 3,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 3,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 3,
                [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                [Fonts.AVENIR_NEXT_LIGHT]: 3,
                [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                [Fonts.CRIMSONPRO_BOLD]: 3,
                [Fonts.CRIMSONPRO_BOLDER]: 3,
                [Fonts.CRIMSONPRO_MEDIUM]: 3,
                [Fonts.HELVETICA]: 3,
                [Fonts.HELVETICA_BOLD]: 6,
                [Fonts.HELVETICA_NARROW]: 3,
                [Fonts.HELVETICA_NARROW_BOLD]: 3
            },
            10: {
                [Fonts.AVENIR_NEXT_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                [Fonts.AVENIR_NEXT_LIGHT]: 2,
                [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                [Fonts.CRIMSONPRO_BOLD]: 2,
                [Fonts.CRIMSONPRO_BOLDER]: 2,
                [Fonts.CRIMSONPRO_MEDIUM]: 2,
                [Fonts.HELVETICA]: 3,
                [Fonts.HELVETICA_BOLD]: 10,
                [Fonts.HELVETICA_NARROW]: 2,
                [Fonts.HELVETICA_NARROW_BOLD]: 2
            },
            11: {
                [Fonts.AVENIR_NEXT_BOLD]: 3,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 1,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 1,
                [Fonts.AVENIR_NEXT_LIGHT]: 3,
                [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                [Fonts.CRIMSONPRO_BOLD]: 3,
                [Fonts.CRIMSONPRO_BOLDER]: 3,
                [Fonts.CRIMSONPRO_MEDIUM]: 3,
                [Fonts.HELVETICA]: 2,
                [Fonts.HELVETICA_BOLD]: 2,
                [Fonts.HELVETICA_NARROW]: 3,
                [Fonts.HELVETICA_NARROW_BOLD]: 3
            },
            12: {
                [Fonts.AVENIR_NEXT_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                [Fonts.AVENIR_NEXT_LIGHT]: 2,
                [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                [Fonts.CRIMSONPRO_BOLD]: 2,
                [Fonts.CRIMSONPRO_BOLDER]: 2,
                [Fonts.CRIMSONPRO_MEDIUM]: 2,
                [Fonts.HELVETICA]: 2,
                [Fonts.HELVETICA_BOLD]: 3,
                [Fonts.HELVETICA_NARROW]: 2,
                [Fonts.HELVETICA_NARROW_BOLD]: 2
            }
        };

        // Browser-specific adjustments
        if (isFirefox) {
            _font_size_adjustments = {
                7: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 2,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                8: {
                    [Fonts.AVENIR_NEXT_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 3,
                    [Fonts.CRIMSONPRO_BOLDER]: 3,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 3
                },
                9: {
                    [Fonts.AVENIR_NEXT_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 3,
                    [Fonts.CRIMSONPRO_BOLDER]: 3,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 3
                },
                10: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 6,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                11: {
                    [Fonts.AVENIR_NEXT_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 1,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 1,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 3,
                    [Fonts.CRIMSONPRO_BOLDER]: 3,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 3
                },
                12: {
                    [Fonts.AVENIR_NEXT_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 3,
                    [Fonts.CRIMSONPRO_BOLDER]: 3,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 3
                }
            };
        } else if (isEdge) {
            // Edge-specific adjustments (if needed)
            // Based on testing, you might need different values
            _font_size_adjustments = {
                7: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 2,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                8: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 2,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                9: {
                    [Fonts.AVENIR_NEXT_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 3,
                    [Fonts.CRIMSONPRO_BOLDER]: 3,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 6,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 3
                },
                10: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 10,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                11: {
                    [Fonts.AVENIR_NEXT_BOLD]: 3,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 1,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 1,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 3,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 3,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 3,
                    [Fonts.CRIMSONPRO_BOLDER]: 3,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 2,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 3
                },
                12: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                }
            };
        } else if (isSafari) {
            // Safari-specific adjustments (if needed)
            // Based on testing, you might need different values
            _font_size_adjustments = {
                7: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                8: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 2,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                9: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                10: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 2,
                    [Fonts.AVENIR_NEXT_LIGHT]: 2,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 2,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 2,
                    [Fonts.HELVETICA]: 3,
                    [Fonts.HELVETICA_BOLD]: 6,
                    [Fonts.HELVETICA_NARROW]: 2,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                11: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 1,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 1,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 2,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                },
                12: {
                    [Fonts.AVENIR_NEXT_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLD]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_BOLDER]: 2,
                    [Fonts.AVENIR_NEXT_CONDENSED_MEDIUM]: 3,
                    [Fonts.AVENIR_NEXT_LIGHT]: 3,
                    [Fonts.AVENIR_NEXT_MEDIUM]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLD]: 2,
                    [Fonts.BARLOW_CONDENSED_BOLDER]: 2,
                    [Fonts.BARLOW_CONDENSED_MEDIUM]: 3,
                    [Fonts.CRIMSONPRO_BOLD]: 2,
                    [Fonts.CRIMSONPRO_BOLDER]: 2,
                    [Fonts.CRIMSONPRO_MEDIUM]: 3,
                    [Fonts.HELVETICA]: 2,
                    [Fonts.HELVETICA_BOLD]: 3,
                    [Fonts.HELVETICA_NARROW]: 3,
                    [Fonts.HELVETICA_NARROW_BOLD]: 2
                }
            };
        }

        return _font_size_adjustments[font_size];
    },

    /**
     * Retrieves the lines of the event description based on the provided parameters.
     *
     * @param {Object} event - The event object.
     * @param {string} font - The font name.
     * @param {number} font_size - The font size.
     * @param {boolean} [first_event=false] - Indicates if it is the first event.
     * @returns {string[]} - The lines of the event description.
     */
    getEventDescriptionLines(event, font, font_size, first_event = false) {
        // Create a unique cache key
        const cacheKey = `${event.description}_${font}_${font_size}_${event.is_bold}_${first_event}`;

        // Check if we already calculated the lines for this event
        if (this.descriptionLinesCache[cacheKey]) {
            return this.descriptionLinesCache[cacheKey];
        }

        // Get the actual font being used
        const fontName = this.getValidFontName(font, event.is_bold);
        let fontWeight = this.getFontWeight(fontName);
        if (event.is_bold && !fontWeight) {
            fontWeight = "bold";
        }

        const description = event.description || "";
        if (!description.trim()) {
            return [];
        }

        let lines = [];

        // Special handling for first event's first line (day number accommodation)
        if (first_event && !event.is_all_day) {
            // Get reduced width for first line of first event
            const firstMaxWidth = this.getEventDescriptionMaxWidth(
                event,
                font,
                font_size,
                true // Using true to get the reduced width for first line
            );

            // Use getWrappedLines for the first line with reduced width
            const firstLineResult = Utils.getWrappedLines(
                description,
                fontName,
                font_size,
                fontWeight,
                firstMaxWidth
            );

            if (firstLineResult.length > 0) {
                const firstLine = firstLineResult[0];
                lines.push(firstLine);

                // Process remaining text with standard width
                const remainingText = description
                    .substring(firstLine.length)
                    .trim();
                if (remainingText) {
                    // Standard width for rest of lines
                    const standardMaxWidth = this.getEventDescriptionMaxWidth(
                        event,
                        font,
                        font_size,
                        false
                    );

                    // Use getWrappedLines for remaining text
                    const wrappedLines = Utils.getWrappedLines(
                        remainingText,
                        fontName,
                        font_size,
                        fontWeight,
                        standardMaxWidth
                    );

                    lines = lines.concat(wrappedLines);
                }
            }
        } else {
            // Standard processing for non-first events or all-day first events
            const maxWidth = this.getEventDescriptionMaxWidth(
                event,
                font,
                font_size,
                false
            );

            // Use getWrappedLines for the description
            lines = Utils.getWrappedLines(
                description,
                fontName,
                font_size,
                fontWeight,
                maxWidth
            );
        }

        // Cache and return
        this.descriptionLinesCache[cacheKey] = lines;
        event.description_lines = lines;
        return lines;
    },

    // Add cache clearing method for when adjustments are changed
    clearDescriptionLinesCache() {
        this.descriptionLinesCache = {};
        Utils.clearWidthCache();
        return true;
    }
};

export default CalendarUtils;

export class CalendarCalculator {
    /**
     * @param {Number} month
     * @param {Number} year
     * @param {String} font
     * @param {Number} font_size
     */
    constructor({ month, year, font, font_size }) {
        this._month = month;
        this._year = year;
        this._font = font;
        this._font_size = font_size;
        this._line_height = 4.5;
        this._start_y = 14.5;
        this._week_count = CalendarUtils.getWeekCount(this._year, this._month);
        this._max_lines = CalendarUtils.getMaxLines(
            this._year,
            this._month,
            this._font_size
        );
        this._dates = CalendarUtils.getMonthDates(this._year, this._month);

        if (
            store.state.config.calendar &&
            store.state.config.calendar.line_heights &&
            store.state.config.calendar.line_heights[this._week_count] &&
            store.state.config.calendar.line_heights[this._week_count][
                this._font_size
            ]
        ) {
            this._line_height =
                store.state.config.calendar.line_heights[this._week_count][
                    this._font_size
                ];
        }
        if (
            store.state.config.calendar &&
            store.state.config.calendar.start_y &&
            store.state.config.calendar.start_y[this._week_count] &&
            store.state.config.calendar.start_y[this._week_count][
                this._font_size
            ]
        ) {
            this._start_y =
                store.state.config.calendar.start_y[this._week_count][
                    this._font_size
                ];
        }
        switch (this._week_count) {
            case 5:
                this._box_height = 45.2;
                break;
            case 6:
                this._box_height = 41.7;
                break;
        }
    }

    /**
     * @returns {moment.Moment[]}
     */
    get dates() {
        return this._dates;
    }

    /**
     * @returns {Number}
     */
    get week_count() {
        return this._week_count;
    }

    get custom_text_max_width() {
        return store.state.config.settings.calendar.custom_text_max_width;
    }

    get valid_font_name() {
        return CalendarUtils.getValidFontName(this._font, this.is_bold);
    }

    get bold_font() {
        return store.state.config.settings.font.bold_map[this._font];
    }

    get is_bold() {
        return CalendarUtils.isBoldFont(this._font);
    }

    get custom_text_max_lines() {
        // Get max lines from config
        const maxLinesConfig =
            store.state.config.settings.calendar.custom_text_max_lines;

        // If we have config values for this font size, use them
        if (maxLinesConfig && maxLinesConfig[this._font_size]) {
            return maxLinesConfig[this._font_size];
        }

        // Fallback to hardcoded values if config isn't available
        switch (this._font_size) {
            case 7:
            case 8:
                return 12;
            case 9:
                return 11;
            case 10:
                return 10;
            case 11:
                return 9;
            case 12:
                return 8;
        }
        return 9;
    }

    get font_weight() {
        return CalendarUtils.getFontWeight(this.valid_font_name);
    }

    /**
     * Check if first line overlaps the date number inside box.
     * @param {Object[]} events
     * @returns {boolean}
     */
    firstLineOverlapsDateNumber(events) {
        let line_count = _.sumBy(
            events,
            (e) =>
                CalendarUtils.getEventDescriptionLines(
                    e,
                    this._font,
                    this._font_size
                ).length
        );
        let event_count = events.length;
        let total_height_used = this._line_height * line_count;
        let margin_per_event = events.length
            ? (this._box_height - total_height_used - this._line_height) /
              event_count
            : 0;
        return margin_per_event < 4.5;
    }

    /**
     * @param {Object} location_key
     * @return {string[]}
     */
    getLocationKeyValueLines(location_key) {
        if (!_.trim(location_key.value)) {
            return [];
        }
        let key_width = Utils.getTextWidth(
            location_key.key,
            this.valid_font_name,
            this._font_size,
            this.font_weight
        );
        let value_max_width = this.custom_text_max_width - key_width - 7;

        // Process the entire text at once with appropriate widths
        // First line will have reduced width, subsequent lines will have full width
        let value = location_key.value;

        // Get wrapped lines properly using first line width
        const firstLineResult = Utils.getWrappedLines(
            value,
            this.valid_font_name,
            this._font_size,
            this.font_weight,
            value_max_width
        );

        if (firstLineResult.length === 0) {
            return [];
        }

        let lines = [firstLineResult[0]];

        // If there's more content beyond the first line
        if (firstLineResult[0].length < value.length) {
            // Get the remaining text using substring
            const remainingText = value
                .substring(firstLineResult[0].length)
                .trim();

            if (remainingText) {
                // Wrap the remaining text with full width
                const remainingLines = Utils.getWrappedLines(
                    remainingText,
                    this.valid_font_name,
                    this._font_size,
                    this.font_weight,
                    this.custom_text_max_width
                );

                lines = lines.concat(remainingLines);
            }
        }

        return lines;
    }

    /**
     * @param {string} custom_text
     * @return {string[]}
     */
    getCustomTextLines(custom_text) {
        return Utils.getWrappedLines(
            custom_text,
            this.valid_font_name,
            this._font_size,
            this.font_weight,
            this.custom_text_max_width
        );
    }
}
